import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Clock, Users, ChefHat, ArrowRight, Star } from 'lucide-react';
import { recipesAPI } from '../../lib/api';
import { logger } from '../../lib/logger';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const MEAL_TYPE_LABELS = {
  entree: 'Entrée',
  plat: 'Plat principal',
  dessert: 'Dessert',
};

export const RecipeOfTheDay = () => {
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await recipesAPI.getRecipeOfTheDay();
        if (!cancelled) setRecipe(res.data);
      } catch (err) {
        // Widget gracefully hides if API fails (no recipes yet, network down, etc.)
        logger.debug('Recipe of the day unavailable:', err?.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <section className="py-16 md:py-24 bg-worldplate-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="aspect-[2/1] md:aspect-[3/1] rounded-3xl bg-worldplate-subtle animate-pulse" />
        </div>
      </section>
    );
  }
  if (!recipe) return null;

  const totalTime = recipe.prep_time + recipe.cook_time;
  const today = new Date().toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long',
  });

  return (
    <section
      className="py-16 md:py-24 bg-worldplate-cream"
      data-testid="recipe-of-the-day-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-8 flex items-center gap-3"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-worldplate-accent/15 border border-worldplate-accent/30 text-worldplate-accent font-medium text-sm">
            <Sparkles className="w-4 h-4" />
            Recette du jour
          </span>
          <span className="text-worldplate-muted text-sm capitalize hidden md:inline">
            {today}
          </span>
        </motion.div>

        <Link to={`/recipes/${recipe.id}`} data-testid="recipe-of-the-day-link">
          <motion.article
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            whileHover={{ y: -4 }}
            className="group grid grid-cols-1 md:grid-cols-5 bg-worldplate-paper rounded-3xl overflow-hidden border border-worldplate-border shadow-sm hover:shadow-2xl transition-all"
          >
            {/* Image */}
            <div className="md:col-span-3 relative overflow-hidden aspect-[16/10] md:aspect-auto md:min-h-[420px]">
              <img
                src={recipe.image_url}
                alt={recipe.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent md:bg-gradient-to-r md:from-transparent md:via-transparent md:to-black/30" />

              {/* Floating badge */}
              <div className="absolute top-5 left-5 flex flex-wrap gap-2">
                <Badge className="bg-worldplate-primary text-white shadow-lg">
                  {recipe.country}
                </Badge>
                <Badge className="bg-worldplate-deep text-white shadow-lg">
                  {MEAL_TYPE_LABELS[recipe.meal_type] || recipe.meal_type}
                </Badge>
              </div>

              {/* Floating "★ recette du jour" pill */}
              <motion.div
                animate={{ rotate: [-3, 3, -3] }}
                transition={{ repeat: Infinity, duration: 4, ease: 'easeInOut' }}
                className="absolute bottom-5 right-5 flex items-center gap-2 px-4 py-2 rounded-full bg-worldplate-accent text-worldplate-deep font-accent text-2xl shadow-xl"
              >
                <Sparkles className="w-5 h-5" />
                Du jour
              </motion.div>
            </div>

            {/* Content */}
            <div className="md:col-span-2 p-8 md:p-10 flex flex-col">
              <h2
                className="font-heading text-3xl md:text-4xl font-bold text-worldplate-deep mb-3 leading-tight group-hover:text-worldplate-primary transition-colors"
                data-testid="recipe-of-the-day-title"
              >
                {recipe.name}
              </h2>
              <p className="text-worldplate-muted leading-relaxed line-clamp-4 mb-6">
                {recipe.description}
              </p>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3 py-4 border-y border-worldplate-border mb-6">
                <div className="text-center">
                  <Clock className="w-5 h-5 text-worldplate-primary mx-auto mb-1" />
                  <p className="text-sm font-semibold text-worldplate-deep">{totalTime} min</p>
                  <p className="text-xs text-worldplate-muted">Total</p>
                </div>
                <div className="text-center">
                  <Users className="w-5 h-5 text-worldplate-primary mx-auto mb-1" />
                  <p className="text-sm font-semibold text-worldplate-deep">{recipe.servings} pers.</p>
                  <p className="text-xs text-worldplate-muted">Portions</p>
                </div>
                <div className="text-center">
                  <ChefHat className="w-5 h-5 text-worldplate-primary mx-auto mb-1" />
                  <div className="flex items-center justify-center gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-3 h-3 ${
                          i < recipe.difficulty
                            ? 'text-worldplate-accent fill-worldplate-accent'
                            : 'text-worldplate-muted/30'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-worldplate-muted mt-1">Difficulté</p>
                </div>
              </div>

              {/* CTA */}
              <Button
                className="w-full bg-worldplate-primary hover:bg-worldplate-primary/90 text-white rounded-full mt-auto gap-2 group/btn"
                data-testid="recipe-of-the-day-cta"
              >
                Découvrir la recette
                <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
              </Button>
            </div>
          </motion.article>
        </Link>
      </div>
    </section>
  );
};

export default RecipeOfTheDay;
